/**
 * The plain surface.
 *
 * Four sections, one screen, no navigation to learn. The audience opens this
 * two or three times in the whole process and wants three answers: what am I
 * protecting, what are we asking for, where do I vote.
 *
 * It is not a smaller workbench. The clause tree, the dock and the redline
 * columns are gone entirely — a hundred and twenty teachers do not need to
 * audit the comparison, they need to know what their union is arguing and why.
 *
 * The rule that shapes it: nothing appears here that has not been read and
 * settled. It used to be stricter — nothing that a person had not personally
 * signed off — and that rule was written for an organisation with staff. There
 * are two representatives, they teach full time, and there are three hundred
 * and sixty comparisons; the rule did not make this page careful, it made it
 * blank, which is its own kind of dishonesty in front of a mass meeting.
 *
 * So a settled comparison appears whoever settled it, and a representative who
 * disagrees takes it back from the Report screen, which removes it from here.
 * What still never appears is a comparison nobody has looked at, or one that
 * was looked at and found to be between two clauses about different subjects.
 *
 * The sections that are still empty say why rather than sitting blank.
 * "Nothing here yet" would read as "there is nothing to protect", when the
 * truth is that conditions are at risk and nobody has been through them.
 */

import type { CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type PlainData } from '../lib/api.js';
import { BASIS_LABEL } from '@tmea/shared';

const TYPE_LABEL: Record<string, string> = {
  protect: 'Keep what we have',
  improve: 'Make it better',
  new: 'Something we do not have',
  correct: 'Fix a mistake in the agreement',
};

/**
 * A cost, and what it is a cost of.
 *
 * This used to append "a year for each full-time teacher" to every figure
 * regardless of what the figure was, which was safe only while no claim had a
 * cost — and none did, because the seeded amounts were sitting in the method
 * column as strings and this never rendered.
 *
 * The basis has to travel with the number. Lifting the conditionally classified
 * entry rate is worth about $8,900 to a teacher on that step and a few dollars
 * per FTE to the school; both are true, and a group that quotes the wrong one
 * in a meeting is corrected by the employer's own arithmetic.
 */
function Money({
  amount,
  basis,
}: {
  amount: number | null;
  basis: PlainData['claims'][number]['costBasis'];
}) {
  if (amount === null) return null;
  return (
    <span className="tabular-nums">
      {amount.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 })}{' '}
      {basis ? BASIS_LABEL[basis] : 'a year'}
    </span>
  );
}

export function Plain() {
  const plain = useQuery({ queryKey: ['plain'], queryFn: api.plain });
  const data: PlainData | undefined = plain.data;

  if (!data) return <p className="p-8 text-[17px] text-[var(--color-muted)]">Loading…</p>;

  return (
    // One column, generous measure, larger than the workbench. This is read
    // once on a phone in a staffroom, not worked in for an hour.
    <main className="mx-auto max-w-[680px] px-6 py-8">
      <h1 className="mb-1 text-[21px] font-medium">Our bargaining position</h1>
      <p className="measure-wide mb-8 text-[15px] leading-relaxed text-[var(--color-muted)]">
        Everything below has been read and settled, some of it by your representatives and some of
        it by the software with your representatives able to overrule it. Anything nobody has been
        through yet is counted rather than shown.
      </p>

      <section className="mb-10">
        <h2 className="mb-1 text-[17px] font-medium">What we are asking for</h2>
        {data.claims.length === 0 ? (
          <p className="measure-wide text-[15px] leading-relaxed text-[var(--color-muted)]">
            Nothing has been endorsed by the group yet. Claims appear here once the representatives
            have agreed to table them.
          </p>
        ) : (
          <ul className="mt-3">
            {data.claims.map((claim) => (
              <li key={claim.ref} className="border-t border-[var(--color-rule)] py-4">
                <p className="label mb-1 text-[11px] text-[var(--color-muted)]">
                  {TYPE_LABEL[claim.type] ?? claim.type}
                  {claim.status === 'tabled' && ' · put to the school'}
                  {claim.status === 'agreed' && ' · agreed'}
                </p>
                <h3 className="mb-1 text-[17px] leading-snug">{claim.title}</h3>

                {claim.rationale && (
                  <p className="measure-wide mb-2 text-[15px] leading-relaxed">{claim.rationale}</p>
                )}

                {claim.costPerFteYear !== null && (
                  <p className="mb-2 text-[15px]">
                    <span className="label text-[11px] text-[var(--color-muted)]">What it costs · </span>
                    <Money amount={claim.costPerFteYear} basis={claim.costBasis} />
                    {claim.costMethod && (
                      // The method line, always. A cost with no method is a
                      // number nobody can defend when the school disputes it.
                      <span className="text-[var(--color-muted)]"> — {claim.costMethod}</span>
                    )}
                    {/*
                      And who worked it out. A figure the software estimated
                      should read as an estimate — not because it is worth less
                      than a person's, but because the first question in the
                      room will be where the number came from and the answer
                      should already be on the page.
                    */}
                    {claim.costDraftedBy === 'ai' && (
                      <span className="label ml-1 text-[11px] text-[var(--color-muted)]">
                        · estimated by AI from the rate tables
                      </span>
                    )}
                  </p>
                )}

                {claim.objections.length > 0 && (
                  <div className="mb-1">
                    <p className="label text-[11px] text-[var(--color-muted)]">
                      What the school will say
                    </p>
                    <ul>
                      {claim.objections.map((objection) => (
                        <li key={objection} className="text-[15px] leading-snug">
                          {objection}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {claim.clauses.length > 0 && (
                  <p className="label text-[11px] text-[var(--color-muted)]">
                    In the agreement at {claim.clauses.join(', ')}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mb-10">
        <h2 className="mb-1 text-[17px] font-medium">What we stand to lose</h2>
        <p className="measure-wide mb-2 text-[15px] leading-relaxed text-[var(--color-muted)]">
          Bargaining a new agreement replaces the floor with the Award. Conditions we have above
          that floor are not automatically kept — they have to be argued for.
        </p>

        {/*
          The count is shown whenever anything is still being checked, not only
          when nothing has been.
          
          It used to sit in the `protect.length === 0` branch, on the reasoning
          that an empty section reads as "there is nothing to protect" and needs
          to say otherwise. True, and it stopped being the whole truth the
          moment the first comparison was signed off: one verified row out of
          thirty-eight rendered a list of one and said nothing at all about the
          other thirty-seven. A section showing one card reads as "there is one
          thing at risk", which is a more confident lie than the empty one it
          replaced.
        */}
        {data.protectWaiting > 0 ? (
          <div
            className="callout callout-strong measure-wide mb-3"
            style={{ '--callout-hue': 'var(--color-defect)' } as CSSProperties}
          >
            <span className="callout-glyph" aria-hidden>
              ◔
            </span>
            <p className="text-[15px] leading-relaxed">
              <strong className="font-medium">
                {data.protectWaiting}{' '}
                {data.protect.length > 0 ? 'more conditions have' : 'conditions have'} not been
                gone through yet
              </strong>{' '}
              and are not shown here. They will appear as each one is read. Being absent from this
              list is not a sign a condition is safe.
            </p>
          </div>
        ) : null}

        {data.protect.length > 0 ? (
          <ul className="mt-3">
            {data.protect.map((item) => (
              <li key={item.number} className="border-t border-[var(--color-rule)] py-3">
                <h3 className="text-[17px] leading-snug">{item.heading}</h3>
                {item.plain && (
                  <p className="measure-wide mt-1 text-[15px] leading-relaxed">{item.plain}</p>
                )}
                {/*
                  Three sentences where there were two, because the two were
                  telling twelve of eighteen conditions the wrong story.

                  "The Award has nothing on this, so there is no minimum to fall
                  back to" was driven by a verdict about the Award alone. For
                  severance pay, notice, personal leave and long service leave
                  the statute is still there, and saying otherwise to a hundred
                  and twenty people is the kind of overstatement an employer's
                  lawyer corrects out loud. The five clauses where nothing
                  really is underneath keep the strong sentence, and are worth
                  believing now.
                */}
                <p className="mt-1 text-[15px]">
                  {item.exposure === 'full' ? (
                    <span className="text-[var(--color-defect)]">
                      Nothing else covers this — not the Award, not the law. If it goes, it goes.
                    </span>
                  ) : item.exposure === 'statute' ? (
                    <span className="text-[var(--color-behind)]">
                      The Award says nothing, but the law still does:{' '}
                      {item.statutoryFloors.map((f) => f.gives).join('; ')}. What is at risk is
                      everything we have above that.
                    </span>
                  ) : (
                    <span className="text-[var(--color-behind)]">
                      The Award covers this but gives less.
                    </span>
                  )}
                </p>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <section className="mb-10">
        <h2 className="mb-1 text-[17px] font-medium">Where to vote</h2>
        {data.polls.length === 0 ? (
          <p className="measure-wide text-[15px] leading-relaxed text-[var(--color-muted)]">
            Nothing to vote on at the moment. You will be asked when the group needs to rank what
            matters most.
          </p>
        ) : (
          <ul className="mt-3">
            {data.polls.map((poll) => (
              <li key={poll.id} className="border-t border-[var(--color-rule)] py-3">
                <p className="text-[17px] leading-snug">{poll.question}</p>
                <p className="mt-1 text-[15px] text-[var(--color-muted)]">
                  {poll.closed
                    ? 'Closed.'
                    : `Open — ${poll.turnout} have answered. Nobody can see how you voted.`}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="mb-1 text-[17px] font-medium">What has been decided</h2>
        {data.decisions.length === 0 ? (
          <p className="measure-wide text-[15px] leading-relaxed text-[var(--color-muted)]">
            Nothing logged yet.
          </p>
        ) : (
          <ul className="mt-3">
            {data.decisions.map((decision) => (
              <li key={`${decision.occurredOn}-${decision.subject}`} className="border-t border-[var(--color-rule)] py-3">
                <p className="text-[15px] leading-snug">{decision.resolution}</p>
                <p className="label text-[11px] text-[var(--color-muted)]">
                  {decision.subject} · {decision.occurredOn}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
